import {ICasinofiedAppLauncherService, ILaunchAppData,} from './casinofied-app-launcher.service.interface';
import {ServiceBase} from '../../ServiceBase';
import {IServiceFactoryExtended} from '../../service-factory-extended.interface';
import {
    ActionID,
    AppIdentifiers,
    ControlCenterOpenSource,
    IAppClosedTopicPayload,
    IAppMaximizedTopicPayload,
    IAppMinimizedTopicPayload, ICasinoFreeToPlaySpecificParameters,
    IChatSpecificParams,
    IControlCenterBaseSpecificParams,
    IControlCenterFullSpecificParams,
    IOpenAppData,
    SequentialCorrelationIdHandler,
    TriggerType
} from '@sparkware/uc-sdk-core';
import {CasinofiedAppFactory} from './casinofied-app-factory';
import {ICasinofiedApplication} from './apps/casinofied-application.interface';
import {STANDARD_TOPIC_HEADER} from '../message-broker/message-broker.consts';
import {Check} from "@sparkware/app-utils";
import {makeObservable, observable, runInAction} from "mobx";
import {getAppSpecificParamsForControlCenter} from './apps/control-center/init-params-helper';


export class CasinofiedAppLauncherService extends ServiceBase implements ICasinofiedAppLauncherService {

    private readonly _appFactory: CasinofiedAppFactory;
    @observable
    private _runningApps: Record<string, ICasinofiedApplication> = {};

    constructor(services: IServiceFactoryExtended) {
        super(services);

        makeObservable(this);

        this._appFactory = new CasinofiedAppFactory(services);

        this._subscribeToTopics();
        this._subscribeToSdkEvents();
        this._subscribeToBrowserEvents();
    }

    private _subscribeToTopics() {
        const {openApp} = this.services.messageBroker.channels.navigation.topics;
        openApp.subscribe(this._openAppHandler);

        const {appClosed, appMaximized, appMinimized, closeIntentResult} = this.services.messageBroker.channels.session.topics;
        appClosed.subscribe(this._appClosedHandler);
        appMinimized.subscribe(this._appMinimizedHandler);
        appMaximized.subscribe(this._appMaximizedHandler);
        closeIntentResult.subscribe((payload) => {
            if (payload.canClose) {
                const app = this._runningApps[payload.appID];
                if (!app) {
                    return;
                }

                this._appClosedHandler({
                    appID: payload.appID,
                    correlationID: payload.correlationID,
                    launchInfo: app.openAppData.launchInfo,
                    closeReason: 'closeIntentResult'
                })
            }
        });
    }

    private _subscribeToSdkEvents() {
        this.services.sdk.events.onBeforeOAuthStateChanged(this._onBeforeOAuthStateChangedHandler);
    }

    private _onBeforeOAuthStateChangedHandler = () => {
        if(this.services.user.isAuthorized) {
            //if the user isAuthorized it means that is about to be logged out
            this.services.messageBroker.channels.identity.topics.logout.publish(STANDARD_TOPIC_HEADER, {} as any);
        }

        this._closeAllApps("automatic_closure", {shouldPreserveAppsState: true});
    }

    private _subscribeToBrowserEvents() {
        this.services.window.subscribeTo.unload(this._onBrowserUnload);
    }

    private _openAppHandler = async (payload: IOpenAppData): Promise<void> => {
        await this._openApp(payload, true);
    }

    private _onBrowserUnload = () => {
        this._closeAllApps("user_closed_app", {shouldPreserveAppsState: false});
    }

    private _sequentialCorrelationIdHandlers: Record<string, SequentialCorrelationIdHandler> = {};

    getSequentialCorrelationIdHandler(bussinesCorrelationId: string) {
        if (!this._sequentialCorrelationIdHandlers[bussinesCorrelationId]) {
            this._sequentialCorrelationIdHandlers[bussinesCorrelationId] = new SequentialCorrelationIdHandler();
        }
        return this._sequentialCorrelationIdHandlers[bussinesCorrelationId];
    }

    private async _openApp(payload: IOpenAppData, shouldReportOpenAppEvent: boolean): Promise<ICasinofiedApplication | null> {
        const runningApp = this._runningApps[payload.appID];
        if (runningApp) {

            if (runningApp.isMinimized) {
                await runningApp.waitForAppToOpen();
                this.services.messageBroker.channels.session.topics.appMaximized.publish(STANDARD_TOPIC_HEADER, {
                    appID: runningApp.appId
                });
            }
            this.services.logger.info(`App ${payload.appID} is already open. Running apps: ${this._runningApps}`);
            return;
        }

        try {

            this.getSequentialCorrelationIdHandler(payload.launchInfo.businessCorrelationID).increment();

            if (shouldReportOpenAppEvent) {
                this.services.casinofiedAnalytics.openAppRequest(payload);
            }

            const app = await this._appFactory.createApplication(payload);
            if (!app.isOpeningInNewWindow()) {
                // we keep running apps in memory to dispose them later, when they are closed
                // only for the apps that are not opened in another window
                runInAction(() => {
                    this._runningApps[payload.appID] = app;
                });

            }
            await app.open();

            return app;
        } catch (err) {
            const app = this._runningApps[payload.appID];
            if (app) {
                runInAction(() => {
                    delete this._runningApps[payload.appID];
                });

                if (app.isMinimized) {
                    app.dispose();
                }
            }

            this.services.logger.error(`Failed to load app: ${payload.appID}`, err);
        }
        return null;
    }

    private _appClosedHandler = (payload: IAppClosedTopicPayload) => {
        const runningApp = this._runningApps[payload.appID];
        if (runningApp) {
            runInAction(() => {
                delete this._runningApps[payload.appID];

            });

            this._cleanUpBusinessCorrelationIdHandler();

            runningApp.dispose();
            if (!runningApp.isLoading) {
                this.services.casinofiedAnalytics.appClosed(payload);
            }
        }
    }

    private _cleanUpBusinessCorrelationIdHandler = () => {
        const runningAppsGroupedByBusinessCorrelationId = Object.values(this._runningApps)
                                                                .filter(runningApp => runningApp.openAppData.launchInfo?.businessCorrelationID)
                                                                .groupByKey(runningApp => runningApp.openAppData.launchInfo.businessCorrelationID);

        for (let businessCorrelationId of Object.keys(this._sequentialCorrelationIdHandlers)) {
            if (!runningAppsGroupedByBusinessCorrelationId[businessCorrelationId]) {
                delete this._sequentialCorrelationIdHandlers[businessCorrelationId];
            }
        }
    }

    private _appMinimizedHandler = (payload: IAppMinimizedTopicPayload): void => {
        const app = this._runningApps[payload.appID];
        if (app) {
            app.minimize(payload.appData);
        }
    }

    private _appMaximizedHandler = (payload: IAppMaximizedTopicPayload): void => {
        this._runningApps[payload.appID]?.maximize();
    }

    private _closeAllApps(closeReason: string, options: {shouldPreserveAppsState: boolean}) {

        const preservedApps: Record<string, ICasinofiedApplication> = {}
        Object.values(this._runningApps).forEach(app => {
            if(app.shouldPreserveStateUponOAuthStateChange && options.shouldPreserveAppsState) {
                preservedApps[app.appId] = app;
            } else {
                this.services.messageBroker.channels.session.topics.appClosed.publish(STANDARD_TOPIC_HEADER, {
                    appID: app.appId,
                    closeReason: closeReason,
                    launchInfo: {
                        appSpecificParameters: app.openAppData.launchInfo?.appSpecificParameters,
                        businessCorrelationID: app.openAppData.launchInfo?.businessCorrelationID,
                        trigger: TriggerType.automaticFlow,
                        sourceAppID: app.appId,
                        sourceAppVersion: "",
                        channel: null
                    }
                });
            }

        });
        runInAction(() => {
            this._runningApps = preservedApps;
        });
    }

    private _launchApp(params: ILaunchAppData, appIdentifier: AppIdentifiers): Promise<ICasinofiedApplication | null> {
        const correlationId = params.businessCorrelationID || this.services.tools.getGuid();
        const clientVersion = this.services.sdk.getSkin().getVersion();
        return this._openApp({
            correlationID: correlationId,
            appID: appIdentifier,
            launchInfo: {
                sequentialCorrelationID: 1, //TODO - remove sequenceId after all widget removes its usage
                containerID: this.services.tools.getGuid(),
                trigger: TriggerType.userSelection,
                appSpecificParameters: params.appSpecificParameters,
                businessCorrelationID: correlationId,
                channel: params.channel,
                sourceAppID: AppIdentifiers.CasinoClient,
                sourceAppVersion: clientVersion,
                clientVersion: clientVersion
            }
        }, false);
    }

    get isThereRunningApps(): boolean {
        return !Check.isEmpty(Object.values(this._runningApps).filter(app => !app.isMinimized));
    }

    async openChat(params: ILaunchAppData<IChatSpecificParams>): Promise<ICasinofiedApplication | null> {
        if(this.services.configuration.servicesConfigs.AppLauncher.apps.CR_CHAT.enabled
            && !this.services.language.isRTLLanguage) {
            return this._launchApp(params, AppIdentifiers.CRChat);
        } else {
          await this.services.externalLinks.openOldChat();
          return null;
        }

    }

    openDepositWidget(params: ILaunchAppData): Promise<ICasinofiedApplication | null> {
        return this._launchApp(params, AppIdentifiers.UnifiedCashier);
    }

    openCashoutWidget(params: ILaunchAppData): Promise<ICasinofiedApplication | null> {
        return this._launchApp(params, AppIdentifiers.UnifiedCashout);
    }

    openTransactionHistoryWidget(params: ILaunchAppData): Promise<ICasinofiedApplication | null> {
        return this._launchApp(params, AppIdentifiers.TransactionHistory);
    }

    openFreeToPlayBonusGames(params: ILaunchAppData<ICasinoFreeToPlaySpecificParameters>): Promise<ICasinofiedApplication | null> {
        return this._launchApp(params, AppIdentifiers.CasinoBonusGameWidget);
    }

    async openHelpCenter(params: ILaunchAppData): Promise<ICasinofiedApplication | null> {
        if(this.services.configuration.servicesConfigs.AppLauncher.apps.CR_HELP_CENTER.enabled) {
            return await this._launchApp(params, AppIdentifiers.CRHelpCenter);
        } else {
            await this.services.externalLinks.openHelpPage();
            return null;
        }

    }
    async openContactUs(params: ILaunchAppData): Promise<ICasinofiedApplication | null> {
        if(this.services.configuration.servicesConfigs.AppLauncher.apps.CR_HELP_CENTER.enabled) {
            return await this._launchApp(params, AppIdentifiers.CRHelpCenter);
        } else {
            await this.services.externalLinks.openContactUs();
            return null;
        }

    }

    async openControlCenterFull(params: ILaunchAppData<IControlCenterFullSpecificParams>): Promise<ICasinofiedApplication | null> {
        if (this.services.miniOrbit.isEnabled) {
            let appSpecificParameters = getAppSpecificParamsForControlCenter<IControlCenterFullSpecificParams>(params.appSpecificParameters, this.services);
            this.services.miniOrbit.openControlCenter({
                actionID: ActionID.openControlCenterFull,
                correlationID: params.businessCorrelationID,
                launchInfo: {
                    ...params,
                    trigger: TriggerType.userSelection,
                    appSpecificParameters
                }
            });
            return null;
        } else {
            if(await this.services.user.ensureUserIsLoggedIn('Control Center')) {
                if(!this.services.controlCenter.isEnabled){
                    throw new Error("Control center is not enabled, and an open attempt was performed.");
                }
                params.appSpecificParameters = {
                    source: ControlCenterOpenSource.Lobby,
                    ...params.appSpecificParameters
                };

                return await this._launchApp(params, AppIdentifiers.ControlCenterFull);
            }
            else
            {
                return null;
            }
        }
    }

    openControlCenterMini(params: ILaunchAppData<IControlCenterBaseSpecificParams>): Promise<ICasinofiedApplication | null> {
        return this._launchApp(params, AppIdentifiers.ControlCenterMini);
    }

}
